import React from 'react';

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

export const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
}) => {
  const sizes = {
    sm: { track: 'w-8 h-4', thumb: 'w-3 h-3', translate: 'translate-x-4' },
    md: { track: 'w-10 h-6', thumb: 'w-5 h-5', translate: 'translate-x-[18px]' },
  };

  const s = sizes[size];

  return (
    <label
      className={`inline-flex items-center gap-3 cursor-pointer select-none ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`
          relative inline-flex shrink-0 ${s.track} items-center rounded-full
          transition-colors duration-150 ease-in-out
          ${checked ? 'bg-accent' : 'bg-line-strong'}
        `}
      >
        <span
          className={`
            inline-block ${s.thumb} transform rounded-full bg-white shadow-sm
            transition-transform duration-150 ease-in-out
            ${checked ? s.translate : 'translate-x-0.5'}
          `}
        />
      </button>
      {label && <span className="text-sm text-fg">{label}</span>}
    </label>
  );
};

interface ToggleGroupProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const ToggleGroup: React.FC<ToggleGroupProps> = ({
  options,
  value,
  onChange,
  className = '',
}) => {
  return (
    <div className={`inline-flex rounded-lg border border-line-strong p-0.5 ${className}`}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={`
            h-8 px-3 text-sm font-medium rounded-md transition-colors duration-150
            ${value === option.value
              ? 'bg-fg text-canvas'
              : 'text-muted hover:text-fg'
            }
          `}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};
